import { useState, useEffect } from 'react'
import * as safe from '../../lib/safeStorage'
import { useAuth } from '../../contexts/AuthContext'
import RegistrationPromptModal from '../RegistrationPromptModal'

type Idea = { 
  title: string; 
  category: string; 
  cost: string; 
  duration: string; 
  photoUrl?: string | null; 
  address?: string | null; 
  phone?: string | null; 
  website?: string | null;
  description?: string | null;
  rating?: number | null;
  isLocked?: boolean;
}

type CardState = {
  idea: Idea | null;
  isLocked: boolean;
  isPlaceholder: boolean;
}

export default function FeelingLuckyPanel() {
  const [cards, setCards] = useState<CardState[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSpinning, setIsSpinning] = useState(false)
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false)
  const { isGuest, guestRolls, incrementGuestRolls } = useAuth()

  // Initialize with placeholder cards
  useEffect(() => {
    if (cards.length === 0) {
      const placeholderCards: CardState[] = [
        { idea: null, isLocked: false, isPlaceholder: true },
        { idea: null, isLocked: false, isPlaceholder: true },
        { idea: null, isLocked: false, isPlaceholder: true }
      ]
      setCards(placeholderCards)
    }
  }, [cards.length])

  const roll = async () => {
    // Check if guest user has reached roll limit
    if (isGuest && guestRolls >= 3) {
      setShowRegistrationPrompt(true)
      return
    }

    setLoading(true)
    setError(null)
    setIsSpinning(true)
    
    try {
      // include zipcode and dateTypes if available for better results
      let zip = ''
      let dateTypes: string[] = []
      try {
        const parsed = JSON.parse(safe.getItem('ds_prefs') || '{}')
        zip = parsed?.zip || ''
        dateTypes = parsed?.dateTypes || []
      } catch {}
      
      // Use guest endpoint for guest users, authenticated endpoint for logged-in users
      const endpoint = isGuest ? '/ideas/feeling-lucky-guest' : '/ideas/feeling-lucky'
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      
      // Only add authorization header for authenticated users
      if (!isGuest) {
        headers['Authorization'] = `Bearer ${safe.getItem('ds_token') || ''}`
      }
      
      // Get the correct API base URL based on environment
      const getApiBaseUrl = () => {
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          console.log('🔍 DEBUG: Current hostname:', hostname);
          if (hostname.includes('dateshake-test')) {
            console.log('🔍 DEBUG: Using test environment API');
            return 'https://dateshake-test.up.railway.app/api';
          } else if (hostname.includes('date-shake.com')) {
            console.log('🔍 DEBUG: Using production environment API');
            return 'https://dateshake-production.up.railway.app/api';
          }
        }
        console.log('🔍 DEBUG: Using fallback API');
        return '/api'; // fallback for local development
      };
      
      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}${endpoint}`;
      console.log('🔍 DEBUG: Full API URL:', fullUrl);
      console.log('🔍 DEBUG: Endpoint:', endpoint);
      
      const res = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ zip, dateTypes })
      })
      
      console.log('🔍 DEBUG: Response status:', res.status);
      console.log('🔍 DEBUG: Response headers:', res.headers);
      
      const responseText = await res.text();
      console.log('🔍 DEBUG: Response text:', responseText.substring(0, 200) + '...');
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('🔍 DEBUG: Parsed JSON data:', data);
      } catch (parseError) {
        console.error('🔍 DEBUG: JSON parse error:', parseError);
        console.error('🔍 DEBUG: Full response text:', responseText);
        throw new Error('Invalid JSON response from server');
      }
      
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch ideas')
      
      // Add a delay for the spinning effect
      setTimeout(() => {
        const newIdeas = data.ideas || []
        
        // Update cards: keep locked ones, replace unlocked ones
        setCards(prevCards => {
          return (prevCards || []).map((card, index) => {
            if (card.isLocked) {
              // Keep locked cards as they are
              return card
            } else {
              // Replace unlocked cards with new ideas
              const newIdea = newIdeas[index] || null
              return {
                idea: newIdea,
                isLocked: false,
                isPlaceholder: newIdea === null
              }
            }
          })
        })
        
        // Increment guest roll counter if this is a guest user
        if (isGuest) {
          incrementGuestRolls()
        }
        
        setIsSpinning(false)
      }, 1500) // 1.5 second spinning animation
      
    } catch (e: any) {
      setError(e.message || 'Failed to fetch ideas')
      setIsSpinning(false)
    } finally {
      setLoading(false)
    }
  }

  const toggleLock = (index: number) => {
    setCards(prevCards => {
      const newCards = [...prevCards]
      newCards[index] = {
        ...newCards[index],
        isLocked: !newCards[index].isLocked
      }
      return newCards
    })
  }

  return (
    <div id="feeling-lucky" className="glass-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-black">Feeling Lucky</h2>
          {isGuest && (
            <p className="text-sm text-black/60 mt-1">
              Rolls remaining: {Math.max(0, 3 - guestRolls)}
            </p>
          )}
        </div>
        <button 
          className="btn-primary px-4 py-2" 
          onClick={roll} 
          disabled={loading || (isGuest && guestRolls >= 3)}
        >
          {loading ? 'Rolling...' : (isGuest && guestRolls >= 3) ? 'Register to Roll More' : 'Roll'}
        </button>
      </div>
      {error && <div className="mt-3 text-red-600">{error}</div>}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(cards || []).map((card, idx) => (
          <div 
            key={idx} 
            className={`rounded-2xl bg-white/90 border border-black/5 shadow overflow-hidden relative ${
              card.isPlaceholder && !isSpinning ? 'animate-pulse' : ''
            } ${
              !isSpinning && card.idea ? 'animate-fade-in' : ''
            }`}
            style={{ 
              animationDelay: card.idea ? `${idx * 0.2}s` : '0s',
              animation: card.idea ? 'fadeInUp 0.6s ease-out forwards' : 'none'
            }}
          >
            {/* Lock/Unlock button */}
            <button
              onClick={() => toggleLock(idx)}
              className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                card.isLocked 
                  ? 'bg-orange-500 text-white hover:bg-orange-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title={card.isLocked ? 'Unlock card' : 'Lock card'}
            >
              {card.isLocked ? '🔒' : '🔓'}
            </button>

            {/* Card Title */}
            <div className="absolute top-2 left-2 z-10">
              <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-semibold text-black/80">
                {idx === 0 ? 'Dining' : idx === 1 ? 'Activity' : 'Entertainment'}
              </div>
            </div>

            {/* Card content */}
            {isSpinning && !card.isLocked ? (
              // Loading animation for unlocked cards
              <>
                <div className="w-full h-32 bg-gradient-to-br from-yellow-200 to-orange-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl mb-2 animate-bounce">🎲</div>
                    <div className="text-sm text-gray-600 font-medium">Rolling...</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </>
            ) : card.isPlaceholder ? (
              // Placeholder content
              <>
                <div className="w-full h-32 bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl mb-2">🎲</div>
                    <div className="text-sm text-gray-600 font-medium">Ready to roll!</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </>
            ) : card.idea ? (
              // Actual idea content
              <>
                {card.idea.photoUrl ? (
                  <img 
                    src={card.idea.photoUrl} 
                    alt={card.idea.title} 
                    className="w-full h-32 object-cover" 
                    onError={(e) => {
                      console.log('Image failed to load:', card.idea?.photoUrl);
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'block';
                      }
                    }}
                  />
                ) : null}
                <div className="w-full h-32 bg-brand-pearl" style={{ display: card.idea.photoUrl ? 'none' : 'block' }}>
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <div className="text-2xl mb-1">
                        {idx === 0 ? '🍽️' : idx === 1 ? '🎯' : '🎭'}
                      </div>
                      <div className="text-xs">No Image</div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-black">{card.idea.title}</div>
                    {card.idea.rating && (
                      <div className="flex items-center gap-1 text-sm text-black/70">
                        <span>⭐</span>
                        <span>{card.idea.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Basic Information */}
                  <div className="text-sm text-black/70 mt-1">{card.idea.category} • {card.idea.duration} • {card.idea.cost}</div>
                  
                  {/* Contact Information */}
                  <div className="mt-3 space-y-1">
                    {card.idea.phone && (
                      <div className="text-sm text-black/70 flex items-center gap-2">
                        <span>📞</span>
                        <a className="underline hover:text-blue-600" href={`tel:${card.idea.phone}`}>{card.idea.phone}</a>
                      </div>
                    )}
                    {card.idea.website && (
                      <div className="text-sm text-black/70 flex items-center gap-2">
                        <span>🌐</span>
                        <a className="underline hover:text-blue-600" href={card.idea.website} target="_blank" rel="noreferrer">Visit Website</a>
                      </div>
                    )}
                    {card.idea.address && (
                      <div className="text-sm text-black/70 flex items-start gap-2">
                        <span>📍</span>
                        <span>{card.idea.address}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* AI Description with Read More */}
                  {card.idea.description && (
                    <div className="mt-3">
                      <div className="text-sm text-black/80 italic">
                        {card.idea.description.length > 150 ? (
                          <>
                            {card.idea.description.substring(0, 150)}...
                            <button 
                              className="text-blue-600 hover:text-blue-800 ml-1 underline"
                              onClick={(e) => {
                                const button = e.target as HTMLButtonElement;
                                const description = button.parentElement?.querySelector('.full-description') as HTMLElement;
                                if (description) {
                                  description.style.display = description.style.display === 'none' ? 'block' : 'none';
                                  button.textContent = button.textContent === 'Read More' ? 'Read Less' : 'Read More';
                                }
                              }}
                            >
                              Read More
                            </button>
                            <div className="full-description" style={{ display: 'none' }}>
                              {card.idea.description.substring(150)}
                            </div>
                          </>
                        ) : (
                          card.idea.description
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        ))}
      </div>
      
      <RegistrationPromptModal 
        isOpen={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
      />
    </div>
  )
}


